import { AssetError, assetNotFound } from './errors.ts';
import { preflightAsset } from './preflight.ts';
import {
  DEFAULT_ASSET_LIMITS,
  type AssetLimits,
  type AssetObjectUrlHandle,
  type AssetRecordV1,
  type AssetRepository,
  type ImportAssetInput,
  type UpdateAssetInput,
} from './types.ts';

interface ObjectUrlFactory {
  create(blob: Blob): string;
  revoke(url: string): void;
}

function defaultObjectUrlFactory(): ObjectUrlFactory {
  if (
    typeof Blob === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function' ||
    typeof URL.revokeObjectURL !== 'function'
  ) {
    throw new AssetError(
      'ASSET_OBJECT_URL_UNAVAILABLE',
      'Este entorno no permite crear URLs temporales revocables.',
    );
  }
  return {
    create: (blob) => URL.createObjectURL(blob),
    revoke: (url) => URL.revokeObjectURL(url),
  };
}

function cloneRecord(record: AssetRecordV1): AssetRecordV1 {
  return JSON.parse(JSON.stringify(record)) as AssetRecordV1;
}

function blobPart(bytes: Uint8Array): ArrayBuffer {
  return Uint8Array.from(bytes).buffer;
}

function validateDisplayName(value: string): string {
  const normalized = value.trim();
  if (!normalized) {
    throw new AssetError('ASSET_CORRUPT', 'El nombre visible del activo no puede quedar vacio.');
  }
  return normalized;
}

function makeObjectUrlHandle(
  assetId: string,
  blob: Blob,
  factory: ObjectUrlFactory,
  onRevoke: (handle: AssetObjectUrlHandle) => void,
): AssetObjectUrlHandle {
  const url = factory.create(blob);
  let revoked = false;
  const handle: AssetObjectUrlHandle = {
    assetId,
    url,
    get revoked() {
      return revoked;
    },
    revoke() {
      if (revoked) return;
      revoked = true;
      factory.revoke(url);
      onRevoke(handle);
    },
  };
  return handle;
}

export interface MemoryAssetRepositoryOptions {
  readonly limits?: AssetLimits;
  readonly objectUrlFactory?: ObjectUrlFactory;
}

export class MemoryAssetRepository implements AssetRepository {
  readonly #limits: AssetLimits;
  readonly #objectUrlFactory?: ObjectUrlFactory;
  readonly #records = new Map<string, AssetRecordV1>();
  readonly #bytesByHash = new Map<string, Uint8Array>();
  readonly #objectUrls = new Set<AssetObjectUrlHandle>();

  constructor(options: MemoryAssetRepositoryOptions = {}) {
    this.#limits = options.limits ?? DEFAULT_ASSET_LIMITS;
    this.#objectUrlFactory = options.objectUrlFactory;
  }

  async import(input: ImportAssetInput): Promise<AssetRecordV1> {
    const preflight = await preflightAsset(input, this.#limits);
    const existing = this.#records.get(preflight.record.assetId);
    if (existing) return cloneRecord(existing);
    if (!this.#bytesByHash.has(preflight.record.contentHash)) {
      this.#bytesByHash.set(preflight.record.contentHash, preflight.bytes.slice());
    }
    this.#records.set(preflight.record.assetId, cloneRecord(preflight.record));
    return cloneRecord(preflight.record);
  }

  async get(assetId: string): Promise<AssetRecordV1 | null> {
    const record = this.#records.get(assetId);
    return record ? cloneRecord(record) : null;
  }

  async getBytes(assetId: string): Promise<Uint8Array | null> {
    const record = this.#records.get(assetId);
    if (!record) return null;
    return this.#bytesByHash.get(record.contentHash)?.slice() ?? null;
  }

  async list(): Promise<readonly AssetRecordV1[]> {
    return Array.from(this.#records.values(), cloneRecord).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt) || a.assetId.localeCompare(b.assetId),
    );
  }

  async update(assetId: string, input: UpdateAssetInput): Promise<AssetRecordV1> {
    const current = this.#records.get(assetId);
    if (!current) throw assetNotFound(assetId);
    const updated: AssetRecordV1 = {
      ...current,
      displayName: validateDisplayName(input.displayName),
      updatedAt: input.now ?? new Date().toISOString(),
    };
    this.#records.set(assetId, updated);
    return cloneRecord(updated);
  }

  async selectPdfPage(assetId: string, pageIndex: number, now?: string): Promise<AssetRecordV1> {
    const current = this.#records.get(assetId);
    if (!current) throw assetNotFound(assetId);
    if (current.metadata.kind !== 'pdf') {
      throw new AssetError('ASSET_FORMAT_UNSUPPORTED', 'Solo un PDF permite elegir pagina.', {
        assetId,
      });
    }
    const bytes = this.#bytesByHash.get(current.contentHash);
    if (!bytes) throw assetNotFound(assetId);
    return this.import({
      bytes,
      fileName: current.originalFileName,
      declaredMimeType: current.mimeType,
      selectedPdfPageIndex: pageIndex,
      now,
    });
  }

  async delete(assetId: string): Promise<boolean> {
    const current = this.#records.get(assetId);
    if (!current) return false;
    this.#records.delete(assetId);
    for (const handle of Array.from(this.#objectUrls)) {
      if (handle.assetId === assetId) handle.revoke();
    }
    const stillReferenced = Array.from(this.#records.values()).some(
      (record) => record.contentHash === current.contentHash,
    );
    if (!stillReferenced) this.#bytesByHash.delete(current.contentHash);
    return true;
  }

  async createObjectUrl(assetId: string): Promise<AssetObjectUrlHandle> {
    const record = this.#records.get(assetId);
    const bytes = await this.getBytes(assetId);
    if (!record || !bytes) throw assetNotFound(assetId);
    const factory = this.#objectUrlFactory ?? defaultObjectUrlFactory();
    const handle = makeObjectUrlHandle(
      assetId,
      new Blob([blobPart(bytes)], { type: record.mimeType }),
      factory,
      (revoked) => this.#objectUrls.delete(revoked),
    );
    this.#objectUrls.add(handle);
    return handle;
  }

  dispose(): void {
    for (const handle of Array.from(this.#objectUrls)) handle.revoke();
  }
}

interface StoredBlob {
  readonly contentHash: string;
  readonly blob: Blob;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
  });
}

export interface IndexedDbAssetRepositoryOptions {
  readonly databaseName?: string;
  readonly limits?: AssetLimits;
  readonly indexedDb?: IDBFactory;
  readonly objectUrlFactory?: ObjectUrlFactory;
}

export class IndexedDbAssetRepository implements AssetRepository {
  readonly #limits: AssetLimits;
  readonly #databaseName: string;
  readonly #indexedDb: IDBFactory;
  readonly #objectUrlFactory?: ObjectUrlFactory;
  readonly #objectUrls = new Set<AssetObjectUrlHandle>();
  readonly #databasePromise: Promise<IDBDatabase>;
  #disposed = false;

  constructor(options: IndexedDbAssetRepositoryOptions = {}) {
    const indexedDb = options.indexedDb ?? globalThis.indexedDB;
    if (!indexedDb) {
      throw new AssetError(
        'ASSET_INDEXEDDB_UNAVAILABLE',
        'IndexedDB no esta disponible en este entorno.',
      );
    }
    this.#indexedDb = indexedDb;
    this.#databaseName = options.databaseName ?? 'dg-embalajes-vnext-assets-v1';
    this.#limits = options.limits ?? DEFAULT_ASSET_LIMITS;
    this.#objectUrlFactory = options.objectUrlFactory;
    this.#databasePromise = this.#open();
  }

  #open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = this.#indexedDb.open(this.#databaseName, 1);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains('records')) {
          database.createObjectStore('records', { keyPath: 'assetId' });
        }
        if (!database.objectStoreNames.contains('blobs')) {
          database.createObjectStore('blobs', { keyPath: 'contentHash' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('No se pudo abrir IndexedDB'));
      request.onblocked = () => reject(new Error('IndexedDB quedo bloqueado por otra pestana'));
    });
  }

  async import(input: ImportAssetInput): Promise<AssetRecordV1> {
    const preflight = await preflightAsset(input, this.#limits);
    const database = await this.#databasePromise;
    const transaction = database.transaction(['records', 'blobs'], 'readwrite');
    const records = transaction.objectStore('records');
    const blobs = transaction.objectStore('blobs');
    const existing = await requestResult(records.get(preflight.record.assetId) as IDBRequest<AssetRecordV1 | undefined>);
    if (!existing) {
      records.put(preflight.record);
      const existingBlob = await requestResult(blobs.get(preflight.record.contentHash) as IDBRequest<StoredBlob | undefined>);
      if (!existingBlob) {
        blobs.put({
          contentHash: preflight.record.contentHash,
          blob: new Blob([blobPart(preflight.bytes)], { type: preflight.record.mimeType }),
        } satisfies StoredBlob);
      }
    }
    await transactionDone(transaction);
    return cloneRecord(existing ?? preflight.record);
  }

  async get(assetId: string): Promise<AssetRecordV1 | null> {
    const database = await this.#databasePromise;
    const transaction = database.transaction('records', 'readonly');
    const result = await requestResult(
      transaction.objectStore('records').get(assetId) as IDBRequest<AssetRecordV1 | undefined>,
    );
    await transactionDone(transaction);
    return result ? cloneRecord(result) : null;
  }

  async getBytes(assetId: string): Promise<Uint8Array | null> {
    const record = await this.get(assetId);
    if (!record) return null;
    const database = await this.#databasePromise;
    const transaction = database.transaction('blobs', 'readonly');
    const stored = await requestResult(
      transaction.objectStore('blobs').get(record.contentHash) as IDBRequest<StoredBlob | undefined>,
    );
    await transactionDone(transaction);
    return stored ? new Uint8Array(await stored.blob.arrayBuffer()) : null;
  }

  async list(): Promise<readonly AssetRecordV1[]> {
    const database = await this.#databasePromise;
    const transaction = database.transaction('records', 'readonly');
    const records = await requestResult(
      transaction.objectStore('records').getAll() as IDBRequest<AssetRecordV1[]>,
    );
    await transactionDone(transaction);
    return records.map(cloneRecord).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt) || a.assetId.localeCompare(b.assetId),
    );
  }

  async update(assetId: string, input: UpdateAssetInput): Promise<AssetRecordV1> {
    const database = await this.#databasePromise;
    const transaction = database.transaction('records', 'readwrite');
    const store = transaction.objectStore('records');
    const current = await requestResult(store.get(assetId) as IDBRequest<AssetRecordV1 | undefined>);
    if (!current) {
      transaction.abort();
      throw assetNotFound(assetId);
    }
    const updated: AssetRecordV1 = {
      ...current,
      displayName: validateDisplayName(input.displayName),
      updatedAt: input.now ?? new Date().toISOString(),
    };
    store.put(updated);
    await transactionDone(transaction);
    return cloneRecord(updated);
  }

  async selectPdfPage(assetId: string, pageIndex: number, now?: string): Promise<AssetRecordV1> {
    const current = await this.get(assetId);
    const bytes = await this.getBytes(assetId);
    if (!current || !bytes) throw assetNotFound(assetId);
    if (current.metadata.kind !== 'pdf') {
      throw new AssetError('ASSET_FORMAT_UNSUPPORTED', 'Solo un PDF permite elegir pagina.', {
        assetId,
      });
    }
    return this.import({
      bytes,
      fileName: current.originalFileName,
      declaredMimeType: current.mimeType,
      selectedPdfPageIndex: pageIndex,
      now,
    });
  }

  async delete(assetId: string): Promise<boolean> {
    const database = await this.#databasePromise;
    const transaction = database.transaction(['records', 'blobs'], 'readwrite');
    const records = transaction.objectStore('records');
    const current = await requestResult(records.get(assetId) as IDBRequest<AssetRecordV1 | undefined>);
    if (!current) {
      await transactionDone(transaction);
      return false;
    }
    records.delete(assetId);
    const allRecords = await requestResult(records.getAll() as IDBRequest<AssetRecordV1[]>);
    if (!allRecords.some((record) => record.contentHash === current.contentHash)) {
      transaction.objectStore('blobs').delete(current.contentHash);
    }
    await transactionDone(transaction);
    for (const handle of Array.from(this.#objectUrls)) {
      if (handle.assetId === assetId) handle.revoke();
    }
    return true;
  }

  async createObjectUrl(assetId: string): Promise<AssetObjectUrlHandle> {
    const record = await this.get(assetId);
    const bytes = await this.getBytes(assetId);
    if (!record || !bytes) throw assetNotFound(assetId);
    const factory = this.#objectUrlFactory ?? defaultObjectUrlFactory();
    const handle = makeObjectUrlHandle(
      assetId,
      new Blob([blobPart(bytes)], { type: record.mimeType }),
      factory,
      (revoked) => this.#objectUrls.delete(revoked),
    );
    this.#objectUrls.add(handle);
    return handle;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    for (const handle of Array.from(this.#objectUrls)) handle.revoke();
    void this.#databasePromise.then((database) => database.close());
  }
}
